import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Palette, Search } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAccountStore } from '../../entities/account';
import { useTemplateStore, type Template } from '../../entities/template';
import { ROUTES } from '../../shared/config/routes';

/** Цветовой отпечаток шаблона — быстрее опознать, чем по названию шрифта. */
function TemplateSwatch({ template }: { template: Template }): JSX.Element {
  const background = template.useGradient && template.backgroundColorEnd
    ? `linear-gradient(135deg, ${template.backgroundColor}, ${template.backgroundColorEnd})`
    : template.backgroundColor;

  return (
    <span
      className="inline-flex h-6 w-10 shrink-0 items-center justify-center rounded-md border text-[10px] font-semibold"
      style={{ background, color: template.textColor }}
      title={`Фон ${template.backgroundColor}, текст ${template.textColor}`}
    >
      Аа
    </span>
  );
}

function TypeCell({ template, accountId, type }: { template?: Template; accountId: string; type: 'reel' | 'post' }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Редактор после «Сохранить» вернёт сюда же — с тем же поиском.
  const state = { from: location.pathname + location.search };

  if (!template) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={() => navigate(`${ROUTES.TEMPLATE_NEW}?accountId=${accountId}&type=${type}`, { state })}
      >
        Настроить
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(`/templates/${template.id}/edit?type=${type}`, { state })}
      className="inline-flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-accent"
    >
      <TemplateSwatch template={template} />
      <span className="text-sm text-muted-foreground">{template.fontName ?? 'Шрифт по умолчанию'}</span>
    </button>
  );
}

/**
 * Вкладка «Шаблоны оформления»: по строке на аккаунт, отдельно Reels и пост.
 * Раньше попасть сюда можно было только через «⋯» на карточке аккаунта —
 * списка шаблонов в приложении не существовало.
 */
export function AccountTemplatesTab(): JSX.Element {
  const accounts = useAccountStore((state) => state.accounts);
  const isAccountsLoading = useAccountStore((state) => state.isLoading);
  const templates = useTemplateStore((state) => state.templates);
  const isTemplatesLoading = useTemplateStore((state) => state.isLoading);
  const fetchTemplates = useTemplateStore((state) => state.fetchTemplates);
  // Поиск — в адресе: из редактора возвращаемся к тому же списку.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const setQuery = (next: string) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('q', next);
      else params.delete('q');
      return params;
    }, { replace: true });

  useEffect(() => {
    if (templates.length === 0) fetchTemplates().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byAccount = useMemo(() => {
    const map = new Map<string, { reel?: Template; post?: Template }>();
    for (const template of templates) {
      const entry = map.get(template.accountId) ?? {};
      entry[template.type] = template;
      map.set(template.accountId, entry);
    }
    return map;
  }, [templates]);

  const rows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return accounts
      .filter((account) =>
        normalized === ''
          ? true
          : `${account.username ?? ''} ${account.name ?? ''}`.toLowerCase().includes(normalized),
      )
      .map((account) => ({ account, ...(byAccount.get(account.id) ?? {}) }));
  }, [accounts, byAccount, query]);

  const configuredCount = useMemo(
    () => accounts.filter((a) => byAccount.has(a.id)).length,
    [accounts, byAccount],
  );

  if (isAccountsLoading && accounts.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск аккаунта"
            className="pl-9"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          Оформление настроено у {configuredCount} из {accounts.length}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Аккаунт</TableHead>
                  <TableHead>Reels 9:16</TableHead>
                  <TableHead>Пост 1080×1350</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-12 text-center text-muted-foreground">
                      <Palette className="mx-auto mb-3 h-10 w-10 opacity-50" />
                      {accounts.length === 0 ? 'Сначала подключите аккаунт' : 'Ничего не найдено'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(({ account, reel, post }) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={account.profilePicture} alt={account.name} />
                            <AvatarFallback>
                              {(account.username ?? account.name ?? '?').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {account.username ? `@${account.username}` : account.name}
                            </p>
                            {!reel && !post && (
                              <Badge variant="outline" className="mt-0.5 text-[10px]">
                                без оформления
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TypeCell template={reel} accountId={account.id} type="reel" />
                      </TableCell>
                      <TableCell>
                        <TypeCell template={post} accountId={account.id} type="post" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isTemplatesLoading && <p className="text-sm text-muted-foreground">Загружаем шаблоны…</p>}
    </div>
  );
}
